import { useState, useMemo } from 'react';
import {
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  Typography,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAppSelector } from 'store/hook';
import TireInventoryHandler from 'handlers/tire-inventory.handler';
import { type InstallTireRequest } from 'types/tire-inventory.types';
import { type Asset } from 'types/asset.types';
import { ThemePalette } from 'mui.theme';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { getDynamicAssetTireInfo } from 'views/TirePressure/util';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { convertDisplayTimeToUtc } from 'utils/helpers/dateTime';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

dayjs.extend(utc);
dayjs.extend(timezone);

interface Props {
  tireSerialNumber: string;
  open: boolean;
  handleAbort: () => void;
  enableDarkTheme: boolean;
  onSuccess?: () => Promise<void>;
}

export const InstallTireModal = ({
  tireSerialNumber,
  open,
  handleAbort,
  enableDarkTheme,
  onSuccess,
}: Props) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const customerId = useAppSelector(
    (state) => state.persistedReducer.customer.id
  );
  const assets = useAppSelector((state) => state.assetReducer.assets);
  const tirePatternPositions = useAppSelector(
    (state) => state.tirePatternReducer.tirePatternPositions
  );
  const makeModelDetails = useAppSelector(
    (state) => state.makeModelDetailsReducer
  );

  const tireInventoryHandler = new TireInventoryHandler();
  const { timezoneBasedOnDisplayMode } = useTimeDisplayMode();

  // Get current date/time in customer's timezone for installation date default
  const getCurrentDateTime = () => {
    const now = dayjs().tz(timezoneBasedOnDisplayMode);
    return now.format('YYYY-MM-DDTHH:mm');
  };

  const [formData, setFormData] = useState<InstallTireRequest>({
    tireSerialNumber,
    assetId: '',
    position: '',
    installationDate: getCurrentDateTime(),
    odometerAtInstall: 0,
    engineHoursAtInstall: 0,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);

  // Get tire positions for selected asset
  const availableTirePositions = useMemo(() => {
    if (
      !selectedAsset ||
      !tirePatternPositions.length ||
      !makeModelDetails.length
    ) {
      return [];
    }

    const { tireCodes } = getDynamicAssetTireInfo(
      makeModelDetails,
      tirePatternPositions,
      selectedAsset.make,
      selectedAsset.model,
      customerId
    );

    return Object.entries(tireCodes).map(([code, label]) => ({
      code,
      label,
    }));
  }, [selectedAsset, tirePatternPositions, makeModelDetails, customerId]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.assetId) {
      newErrors.assetId = 'Asset is required';
    }
    if (!formData.position) {
      newErrors.position = 'Tire position is required';
    }
    if (!formData.installationDate) {
      newErrors.installationDate = 'Installation date is required';
    }
    if (
      formData.odometerAtInstall === undefined ||
      formData.odometerAtInstall === null
    ) {
      newErrors.odometerAtInstall = 'Odometer reading is required';
    } else if (formData.odometerAtInstall < 0) {
      newErrors.odometerAtInstall = 'Must be a positive number';
    }
    if (
      formData.engineHoursAtInstall === undefined ||
      formData.engineHoursAtInstall === null
    ) {
      newErrors.engineHoursAtInstall = 'Engine hours is required';
    } else if (formData.engineHoursAtInstall < 0) {
      newErrors.engineHoursAtInstall = 'Must be a positive number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: keyof InstallTireRequest, value: any) => {
    setFormData({
      ...formData,
      [field]: value === '' ? undefined : value,
    });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleAssetChange = (assetId: string) => {
    const asset = assets.find((a) => a.assetId === assetId);
    setSelectedAsset(asset ?? null);
    setFormData({
      ...formData,
      assetId,
      position: '', // Reset tire position when asset changes
    });
    if (errors.assetId) {
      setErrors({ ...errors, assetId: '', position: '' });
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    // Convert installation date to UTC before sending
    const dataToSend = {
      ...formData,
      installationDate: convertDisplayTimeToUtc(
        formData.installationDate,
        'configured',
        timezoneBasedOnDisplayMode
      ).toISOString(),
    };

    const success = await tireInventoryHandler.installTire(
      dataToSend,
      customerId
    );

    if (success) {
      // Wait for the inventory to refresh before closing the modal
      await onSuccess?.();
      handleAbort();
    }
  };

  const isFormValid =
    !!formData.assetId &&
    !!formData.position &&
    !!formData.installationDate &&
    formData.odometerAtInstall !== undefined &&
    formData.engineHoursAtInstall !== undefined;

  return (
    <Dialog
      open={open}
      onClose={handleAbort}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: enableDarkTheme
            ? palette.modalBackground
            : palette.boxBackground,
          backgroundImage: 'none',
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pt: 2.5,
          px: 3,
          color: palette.textPrimary,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            component="div"
            sx={{ fontWeight: 600, mb: 0.5 }}
          >
            {t('install_tire_to_asset')}
          </Typography>
          <Typography
            variant="body2"
            sx={{ opacity: 0.7, fontSize: '0.875rem' }}
          >
            {t('tire_serial_number_2')} <strong>{tireSerialNumber}</strong>
          </Typography>
        </Box>
        <IconButton
          onClick={handleAbort}
          sx={{
            color: palette.textPrimary,
            '&:hover': {
              backgroundColor: enableDarkTheme
                ? 'rgba(255, 255, 255, 0.08)'
                : 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 3, px: 3 }}>
        <Grid container spacing={2} mt={1}>
          {/* Select Asset */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required error={!!errors.assetId}>
              <InputLabel
                sx={{
                  color: enableDarkTheme
                    ? ThemePalette.typography.white
                    : 'rgba(0, 0, 0, 0.6)',
                }}
              >
                {t('select_asset')}
              </InputLabel>
              <Select
                value={formData.assetId ?? ''}
                label={t('select_asset')}
                onChange={(e) => {
                  handleAssetChange(e.target.value);
                }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1.5px',
                  },
                  color: palette.textPrimary,
                }}
              >
                {assets.map((asset) => (
                  <MenuItem key={asset.assetId} value={asset.assetId}>
                    {asset.bumperNumber} - {asset.make} {asset.model}
                  </MenuItem>
                ))}
              </Select>
              {errors.assetId && (
                <FormHelperText>{errors.assetId}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          {/* Tire Position */}
          <Grid item xs={12} sm={6}>
            <FormControl
              fullWidth
              required
              error={!!errors.position}
              disabled={
                !formData.assetId || availableTirePositions.length === 0
              }
            >
              <InputLabel
                sx={{
                  color: enableDarkTheme
                    ? ThemePalette.typography.white
                    : 'rgba(0, 0, 0, 0.6)',
                }}
              >
                {t('tire_position')}
              </InputLabel>
              <Select
                value={formData.position ?? ''}
                label={t('tire_position')}
                onChange={(e) => {
                  handleChange('position', e.target.value);
                }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1.5px',
                  },
                  color: palette.textPrimary,
                }}
              >
                {availableTirePositions.length === 0 ? (
                  <MenuItem value="" disabled>
                    {formData.assetId
                      ? 'No tire positions available'
                      : 'Select an asset first'}
                  </MenuItem>
                ) : (
                  availableTirePositions.map(
                    (position: { code: string; label: string }) => (
                      <MenuItem key={position.code} value={position.label}>
                        {position.label}
                      </MenuItem>
                    )
                  )
                )}
              </Select>
              {errors.position && (
                <FormHelperText>{errors.position}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          {/* Installation Date */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              type="datetime-local"
              label={t('installation_date_2')}
              value={formData.installationDate ?? ''}
              onChange={(e) => {
                handleChange('installationDate', e.target.value);
              }}
              error={!!errors.installationDate}
              helperText={
                errors.installationDate ?? 'When the tire was installed'
              }
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderWidth: '1.5px',
                  },
                },
              }}
            />
          </Grid>

          {/* Odometer at Install */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              type="number"
              label={t('odometer_at_install')}
              placeholder={t('e_g_50000')}
              value={formData.odometerAtInstall ?? ''}
              onChange={(e) => {
                handleChange(
                  'odometerAtInstall',
                  e.target.value ? parseFloat(e.target.value) : undefined
                );
              }}
              error={!!errors.odometerAtInstall}
              helperText={
                errors.odometerAtInstall ?? 'Odometer reading at installation'
              }
              inputProps={{ min: 0, step: 1 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderWidth: '1.5px',
                  },
                },
              }}
            />
          </Grid>

          {/* Engine Hours at Install */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              type="number"
              label={t('engine_hours_at_install')}
              placeholder={t('e_g_2500')}
              value={formData.engineHoursAtInstall ?? ''}
              onChange={(e) => {
                handleChange(
                  'engineHoursAtInstall',
                  e.target.value ? parseFloat(e.target.value) : undefined
                );
              }}
              error={!!errors.engineHoursAtInstall}
              helperText={
                errors.engineHoursAtInstall ?? 'Engine hours at installation'
              }
              inputProps={{ min: 0, step: 0.1 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderWidth: '1.5px',
                  },
                },
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2.5, gap: 1.5 }}>
        <Button
          onClick={handleAbort}
          variant="outlined"
          size="large"
          sx={{
            color: enableDarkTheme
              ? ThemePalette.typography.white
              : ThemePalette.typography.primary,
            borderColor: enableDarkTheme
              ? ThemePalette.typography.white
              : ThemePalette.typography.primary,
            borderWidth: '1.5px',
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            '&:hover': {
              borderWidth: '1.5px',
              backgroundColor: enableDarkTheme
                ? 'rgba(255, 255, 255, 0.08)'
                : 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          {t('cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          size="large"
          disabled={!isFormValid}
          sx={{
            backgroundColor: ThemePalette.typography.primary,
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            boxShadow: 2,
            '&:hover': {
              backgroundColor: ThemePalette.typography.primary,
              opacity: 0.9,
              boxShadow: 3,
            },
            '&:disabled': {
              backgroundColor: enableDarkTheme
                ? 'rgba(255, 255, 255, 0.12)'
                : 'rgba(0, 0, 0, 0.12)',
            },
          }}
        >
          {t('install_tire')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
